import type { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import type { ProjectAdminRow } from '../models/project-admin'
import { ProjectLogo } from './project-badge'

export const PROJECTS_ADMIN_COLUMN_NAMES: Record<string, string> = {
  name: 'Proyecto',
  code: 'Código',
  societyName: 'Sociedad',
  currency: 'Moneda',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function ProjectActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activo' : 'Inactivo'}
    </span>
  )
}

export function ProjectCurrencyBadge({ currency }: { currency: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/70">
      {currency}
    </span>
  )
}

export const projectsAdminColumns: ColumnDef<ProjectAdminRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Proyecto" />
    ),
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        <ProjectLogo name={row.original.name} code={row.original.code} />
        <span className="truncate font-medium" title={row.original.name}>
          {row.original.name}
        </span>
      </span>
    )
  },
  {
    accessorKey: 'code',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Código" />,
    cell: ({ row }) => <span className="whitespace-nowrap">{row.original.code}</span>
  },
  {
    accessorKey: 'societyName',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Sociedad" />
    ),
    cell: ({ row }) =>
      row.original.societyName ? (
        <span className="block max-w-[18rem] truncate" title={row.original.societyName}>
          {row.original.societyName}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )
  },
  {
    accessorKey: 'currency',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Moneda" sortable={false} />
    ),
    cell: ({ row }) => <ProjectCurrencyBadge currency={row.original.currency} />
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <ProjectActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
